import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL } from '../../constants';
import { storage } from '../../utils';
import { markWorkflowWrite } from './workflowReadRequests';

const RETRY_AFTER_DEFAULT_MS = 1000;
const RETRY_AFTER_MAX_MS = 10000;
/** 503 ⇒ số ms đợi trước khi thử lại một lần (đọc `Retry-After` giây hoặc ngày HTTP; mặc định 1 s, tối đa 10 s). Khác 503 ⇒ null (không thử lại). */
export function retryAfterMsOf(error: any): number | null {
  if (error?.response?.status !== 503) return null;
  const raw = error.response.headers?.['retry-after'] ?? error.response.headers?.['Retry-After'];
  let ms = RETRY_AFTER_DEFAULT_MS;
  if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
    const secs = Number(raw);
    if (Number.isFinite(secs)) ms = secs * 1000;
    else { const at = Date.parse(String(raw)); if (Number.isFinite(at)) ms = at - Date.now(); }
  }
  return Math.max(0, Math.min(RETRY_AFTER_MAX_MS, ms));
}

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 600000, // 5 minutes
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        const token = storage.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        const status = error.response?.status;
        // 401 / 403 ⇒ đăng xuất (token hỏng / hết hạn / tài khoản bị khoá — mọi lời gọi sau đó đều lỗi). 503 (BE bận / vừa khởi động lại, kèm Retry-After) và lỗi mạng KHÔNG đăng xuất: ném lỗi để màn hiện «thử lại».
        if (status === 401 || status === 403) {
          storage.clear();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.client.get(url, config);
      return response.data;
    } catch (error) {
      // 503 + Retry-After: ĐỌC (GET / poll) thử lại ĐÚNG MỘT lần. Việc ghi (POST/PUT/PATCH/DELETE — có thể tốn tiền) KHÔNG bao giờ tự thử lại.
      const waitMs = retryAfterMsOf(error);
      if (waitMs === null) throw error;
      await new Promise((resolve) => setTimeout(resolve, waitMs));
      const response: AxiosResponse<T> = await this.client.get(url, config);
      return response.data;
    }
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.client.post(url, data, config);
      return response.data;
    } finally { markWorkflowWrite(); }
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.client.put(url, data, config);
      return response.data;
    } finally { markWorkflowWrite(); }
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.client.patch(url, data, config);
      return response.data;
    } finally { markWorkflowWrite(); }
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.client.delete(url, config);
      return response.data;
    } finally { markWorkflowWrite(); }
  }

  async upload<T>(url: string, formData: FormData, onProgress?: (progress: number) => void): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.client.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      });
      return response.data;
    } finally { markWorkflowWrite(); }
  }
  
  async getBinary(url: string): Promise<Blob> {
    const response = await this.client.get(url, {
      responseType: 'blob',
    });
    return response.data;
  }

  async uploadPut<T>(url: string, formData: FormData, onProgress?: (progress: number) => void): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.client.put(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      });
      return response.data;
    } finally { markWorkflowWrite(); }
  }
}

export const apiClient = new ApiClient();


